/**
 * Flow (workflow/process) operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class FlowManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listCategories(): Promise<Record<string, any>> {
    return this.session.post('/api/flow/category/list-all', { data: null });
  }

  async listForms(projectId?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = {};
    if (projectId) params.projectId = projectId;
    return this.session.get('/api/flow/form/list-all', { params });
  }

  async getForm(formId: string): Promise<Record<string, any>> {
    return this.session.get(`/api/flow/form/${formId}`);
  }

  async listProcesses(
    page = 1,
    size = 20,
    name?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { current: page, size };
    if (name) data.name = name;
    return this.session.post('/api/flow/process/list-launch', { data });
  }

  async getProcess(processId: string): Promise<Record<string, any>> {
    return this.session.get(`/api/flow/process/configure/${processId}`);
  }

  async launchProcess(processId: string, data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process/extend/launch', { data: data ?? {} });
  }

  async copyProcess(processId: string): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process/extend/copy', { params: { id: processId } });
  }

  async deleteProcess(processId: string): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process/extend/delete', { params: { id: processId } });
  }

  async getPendingTasks(page = 1, size = 20, data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/page-pending-approval', {
      data: { page, pageSize: size, searchCount: false, data: data ?? {} },
    });
  }

  async getTaskCount(): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/count-pending-approval', { data: null });
  }

  async approveTask(taskId: string, data?: Record<string, any>, formId?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = {};
    if (formId) params.formId = formId;
    return this.session.post('/api/flow/process-task/extend/consent', { data: data ?? {}, params });
  }

  async rejectTask(taskId: string, data?: Record<string, any>, formId?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = {};
    if (formId) params.formId = formId;
    return this.session.post('/api/flow/process-task/extend/rejection', { data: data ?? {}, params });
  }

  // --- Extended flow/process APIs ---

  async listCategoriesExtend(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/flow/category/list-all', { params });
  }

  async createCategory(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/category/create', { data });
  }

  async updateCategory(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/category/update', { data });
  }

  async removeCategory(categoryId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/flow/category/remove/${categoryId}`);
  }

  async listFormCategories(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/flow/form-category/list-all', { params });
  }

  async createFormCategory(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/form-category/create', { data });
  }

  async updateFormCategory(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/form-category/update', { data });
  }

  async removeFormCategory(categoryId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/flow/form-category/remove/${categoryId}`);
  }

  async listFormsExtend(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/flow/form/list-all', { params });
  }

  async getFormExtend(formId: string): Promise<Record<string, any>> {
    return this.session.get(`/api/flow/form/${formId}`);
  }

  async createForm(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/form/create', { data });
  }

  async updateForm(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/form/extend/update', { data });
  }

  async removeForm(formId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/flow/form/remove/${formId}`);
  }

  async getFormMeta(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/flow/process/extend/get-form-meta', { params });
  }

  async getChildFormMeta(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/flow/process/extend/get-child-form-meta', { params });
  }

  async createProcess(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process/extend/create', { data });
  }

  async getProcessHistory(processId: string, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get(`/api/flow/process/history/${processId}`, { params });
  }

  async configureProcess(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process/configure', { data });
  }

  async getLaunchedConfigure(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/flow/process/launched_configure', { params });
  }

  async getConfigure(configId: string): Promise<Record<string, any>> {
    return this.session.get(`/api/flow/process/configure/${configId}`);
  }

  async copyProcessExtend(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process/extend/copy', { data });
  }

  async deleteProcessExtend(processId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/flow/process/extend/delete/${processId}`);
  }

  async getProcessForm(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/flow/process/get-form', { params });
  }

  async saveProcessForm(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process/extend/save-form', { data });
  }

  async checkFormChange(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/form/extend/checkFormChange', { data });
  }

  async updateProcessState(processId: string, data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post(`/api/flow/process/update-state-${processId}`, { data });
  }

  async getFormData(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/flow/process/extend/get-form-data', { params });
  }

  async updateFormData(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process/extend/update-form-data', { data });
  }

  async listPendingClaimTasks(page = 1, size = 20, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/page-pending-claim', {
      data: { page, pageSize: size, searchCount: false, data: params ?? {} },
    });
  }

  async listApprovedTasks(page = 1, size = 20, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/page-approved', {
      data: { page, pageSize: size, searchCount: false, data: params ?? {} },
    });
  }

  async listMyApplications(page = 1, size = 20, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/page-my-application', {
      data: { page, pageSize: size, searchCount: false, data: params ?? {} },
    });
  }

  async listMyReceivedTasks(page = 1, size = 20, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/page-my-received', {
      data: { page, pageSize: size, searchCount: false, data: params ?? {} },
    });
  }

  async countPendingApproval(projectId?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = {};
    if (projectId) params.projectId = projectId;
    return this.session.post('/api/flow/process-task/count-pending-approval', { params });
  }

  async claimTask(taskId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/flow/process-task/claim-${taskId}`);
  }

  async reclaimTask(taskId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/flow/process-task/reclaim-${taskId}`);
  }

  async getApprovalInfo(data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/approval-info', { data: data ?? {} });
  }

  async rejectTaskExtend(data: Record<string, any>, formId?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = {};
    if (formId) params.formId = formId;
    return this.session.post('/api/flow/process-task/extend/rejection', { data, params });
  }

  async markViewed(taskId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/flow/process-task/viewed-${taskId}`);
  }

  async commentTask(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/comment', { data });
  }

  async consentTask(data: Record<string, any>, formId?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = {};
    if (formId) params.formId = formId;
    return this.session.post('/api/flow/process-task/extend/consent', { data, params });
  }

  async revokeTask(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/revoke', { data });
  }

  async transferTask(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/transfer', { data });
  }

  async delegateTask(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/delegate', { data });
  }

  async rejectTaskDirect(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/reject', { data });
  }

  async listHisTasks(instanceId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/flow/process-task/list-his-task/${instanceId}`);
  }

  async getNextTasks(data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/flow/process-task/extend/getNextTasks', { data: data ?? {} });
  }
}
